import React, { useState, useEffect, useCallback } from 'react';
import Pagination from '../Pagination';
import axios from '../../api/api';
import { MdOutlineBlock } from 'react-icons/md';
import Loader from '../Loader';
import * as XLSX from "xlsx";

const SuccessfulReports = () => {

  const [successfulRequests, setSuccessfulRequests] = useState([]);
  const [searchAccountNumber, setSearchAccountNumber] = useState('');
  const [searchTransaction, setSearchTransaction] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(20);
  const [count, setCount] = useState(0)
  const [total, setTotal] = useState(0)
  const [product_id, setProduct_id] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Convert date to partition format (pYYYYMMDD)
  const formatDateToPartition = (date) => {
    const d = new Date(date);
    return `p${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
  };

  // Fetch successful reports
  const getSuccessfulReports = useCallback(async (offset, limit) => {
    try {
      let query = `?offset=${offset}&limit=${limit}`

      if (product_id) {
        query += `&service_type=${product_id}`
      }

      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Check if endDate is before startDate
        if (end < start) {
          alert('End date cannot be earlier than start date');
          setLoading(false);
          return;
        }
        const diffTime = Math.abs(end - start);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays > 32) {
          alert('Date range cannot exceed 32 days');
          setLoading(false);
          return;
        }

        const partitions = [];
        let current = new Date(startDate);

        // Handle case when start and end are the same
        if (diffDays === 0) {
          let current = new Date(startDate);
          current.setDate(current.getDate() + 1);
          partitions.push(formatDateToPartition(current));
        } else {
          while (current <= end) {
            current.setDate(current.getDate() + 1);
            partitions.push(formatDateToPartition(current));
          }
        }
        query += `&partitions=${partitions.join(',')}`;
      } else if (startDate) {
        const partitions = [];
        let current = new Date(startDate);
        current.setDate(current.getDate() + 1);
        partitions.push(formatDateToPartition(current));
        query += `&partitions=${partitions.join(',')}`;
      }

      if (searchAccountNumber) query += `&account_no=${searchAccountNumber}`;
      if (searchTransaction) query += `&payment_ref=${searchTransaction}`;

      const response = await axios.get(`/request_log/archive${query}`);
      setSuccessfulRequests(response.data.items);
      setCount(response.data.count)
      setTotal(response.data.total)
      setLoading(false);
      setError(null);
    } catch (error) {
      console.log(error);
      setLoading(false);
      if (!error.response) {
        setError("Network error! Check your connection.");
      } else if (error.response.status) {
        setError(error.response.data.detail)
      } else if (error.response.status >= 500) {
        setError("Server error! Please try again later.");
      } else {
        setError("An unexpected error occurred!");
      }
    }
  }, [product_id, startDate, endDate, searchAccountNumber, searchTransaction]);

  useEffect(() => {
    const timerId = setTimeout(() => {
      setLoading(true);
      getSuccessfulReports((currentPage - 1) * recordsPerPage, recordsPerPage, searchAccountNumber, searchTransaction);
    }, 1000);
    return () => clearTimeout(timerId);
  }, [currentPage, recordsPerPage, getSuccessfulReports, searchAccountNumber, searchTransaction]);


  const handleExport = () => {
    const workbook = XLSX.utils.book_new();
    const excelData = successfulRequests.map((row) => {
      return {
        "MSISDN": row.msisdn,
        "Amount": row.amount,
        "Provider": row.provider,
        "Narration": row.narration,
        "Transaction Date": new Date(row.tran_date).toLocaleString("en-CA", { hour12: false }),
        "Created Date": new Date(row.date_created).toLocaleString("en-CA", { hour12: false }), 
      };
    });
  
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    XLSX.utils.book_append_sheet(workbook, worksheet, "Successful Requests");
    XLSX.writeFile(workbook, "successful_requests.xlsx");
  };


  return (
    <div className='mx-auto p-4'>
      <h4 className="my-2"><b>Successful Requests</b></h4>
      <div className="bg-white rounded-lg p-4 w-full flex-1 min-w-0">
        <div className="flex flex-wrap items-center justify-between py-2">
          <form className='flex flex-wrap items-center'>
            <div className='p-1 w-full lg:w-1/4'>
              <input
                type='search'
                className='px-3 py-1 w-full border bg-[#eef1ff] border-slate-300 rounded-md'
                placeholder='Search account number'
                value={searchAccountNumber}
                onChange={(e) => setSearchAccountNumber(e.target.value)}
              />
            </div>
            <div className='p-1 w-full lg:w-1/4'>
              <input
                type='search'
                className='px-3 py-1 w-full border bg-[#eef1ff] border-slate-300 rounded-md'
                placeholder='Search transaction ID'
                value={searchTransaction}
                onChange={(e) => setSearchTransaction(e.target.value)}
              />
            </div>
            <label htmlFor="" className='p-1 w-full lg:w-1/4'>
              <span className='hidden'>Start Date</span>
              <input 
                type="date"
                className='px-2 py-1 w-full border bg-[#f2f9ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#165277] focus:ring-[#165277] focus:ring-1'
                value={startDate}
                onChange={(e) => {setStartDate(e.target.value)}}
              />
            </label>
            <label htmlFor="" className='p-1 w-full lg:w-1/4'>
              <span className='hidden'>End Date</span>
              <input 
                type="date" 
                className='px-2 py-1 w-full border bg-[#f2f9ff] border-slate-300 placeholder-slate-400 rounded-md focus:outline-none focus:border-[#165277] focus:ring-[#165277] focus:ring-1'
                value={endDate}
                onChange={(e) => {setEndDate(e.target.value)}}
              />
            </label>
          </form>
          <div className='flex items-center gap-2 p-1'>
            <button className='bg-[#134463] text-white px-4 py-1 rounded-lg hover:bg-[#165277]' onClick={handleExport}>Export</button>
            <select
              className='px-3 py-1 border bg-[#f2f9ff] border-slate-300 rounded-md focus:outline-none focus:border-[#165277] focus:ring-[#165277]'
              value={product_id}
              onChange={(e) => {setProduct_id(e.target.value)}}
            >
              <option value=''>Service Type</option>
              <option value='2'>Airtime</option>
              <option value='5'>Bundle</option>
            </select>
          </div>
        </div>
        {loading ? (
          <div className='flex items-center justify-center bg-[#e9f3f9] h-[70vh]'>
            <Loader />
          </div>
        ) : error ? (
          <div className='bg-[#e9f3f9] grid place-items-center h-[70vh]'>
            <div className='grid place-items-center text-red-600 p-4'>
              <h3><MdOutlineBlock /></h3>
              <span>{error}</span>
            </div>
          </div>
        ) : (
          <div className='overflow-x-auto rounded-lg'>
            {successfulRequests.length > 0
              ? (
                <table className='w-full text-left table-auto'>
                  <thead>
                    <tr className='border-b border-slate-500'>
                      <th className='p-2'>Transaction Id</th>
                      <th className='p-2'>Phone Number</th>
                      <th className='p-2'>Account Number</th>
                      <th className='p-2'>Amount</th>
                      <th className='p-2'>Provider</th>
                      <th className='p-2'>Narration</th>
                      <th className='p-2'>Transaction Date</th>
                      <th className='p-2'>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {successfulRequests.map((request) => (
                      <tr key={request.id}>
                        <td className='p-2'>{request.payment_ref}</td>
                        <td className='p-2'>{request.msisdn}</td>
                        <td className='p-2'>{request.account_no}</td>
                        <td className='p-2'>{request.amount}</td>
                        <td className='p-2'>{request.provider}</td>
                        <td className='p-2'>{request.narration}</td>
                        <td className='p-2'>{new Date(request.tran_date).toLocaleString("en-CA", { hour12: false })}</td>
                        <td className='p-2'>{new Date(request.date_created).toLocaleString("en-CA", { hour12: false })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className='bg-[#e9f3f9] text-[#134463] grid place-items-center h-[70vh]'>
                  <div className='grid place-items-center p-4'>
                    <h3><MdOutlineBlock /></h3>
                    <h4>No Data</h4>
                  </div>
                </div>
              )}
          </div>
        )}
        <div className='flex flex-wrap items-center justify-between mt-4 gap-4'>
          <div>
            <span className='mr-2'>Records per page:</span>
            <select
              className='px-3 py-1 border bg-[#e9f3f9] border-slate-300 rounded-md focus:outline-none focus:border-[#165277] focus:ring-[#165277] focus:ring-1'
              value={recordsPerPage}
              onChange={(e) => {
                setRecordsPerPage(parseInt(e.target.value, 10));
                setCurrentPage(1);
              }}
            >
              <option value='20'>20</option>
              <option value='50'>50</option>
              <option value='75'>75</option>
              <option value='100'>100</option>
            </select>
          </div>
          <div>
            <span className='font-bold text-[#165277]'> Showing {count} / {total} records</span>
          </div>
          <Pagination
            nPages={Math.ceil(total / recordsPerPage)}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
        </div>
      </div>
    </div>
  );
};

export default SuccessfulReports;
